import React, { useCallback, useEffect, useState } from "react";
import { Pencil, Plus } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import DashboardLayout from "src/components/layout/dashboard/Layout";
import TableV2 from "src/components/ui/TableV2";
import PageHeader from "src/components/common/PageHeader";
import CardHeader from "src/components/common/card/CardHeader";
import Button from "src/components/ui/Button";
import Card from "src/components/common/card/Card";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as salesActions from "src/redux/slices/salesSlice";
import { formatDateTime } from "src/utils/date-time";
import { currencyFormatter, formatCreatedBy } from "src/utils/common";
import usePlanUsage from "src/hooks/usePlanUsage";
import { Badge } from "src/components/ui/Badge";

const SalesListPage: React.FC = () => {
  const [limit] = useState(10);

  const { salesList } = useAppSelector((state) => state.sales);
  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);
  const permissions = user?.roleDetails?.permissions || user?.permissions || {};
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const canCreateSales =
    isTenantAdmin || (permissions?.sales || []).includes("create");
  const canEditSales =
    isTenantAdmin || (permissions?.sales || []).includes("edit");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { planUsage } = usePlanUsage();

  const saleLimit = planUsage?.plan?.features?.totalSaleOrders;
  const transactionLimit = planUsage?.plan?.features?.totalTransactions;
  const salesUsage = planUsage?.usage?.totalSaleOrders;
  const transactionUsage = planUsage?.usage?.totalTransactions;
  const isPlanExpired = planUsage?.planStatus === "expired";
  const isSaleLimitReached =
    typeof saleLimit === "number" &&
    typeof salesUsage === "number" &&
    salesUsage >= saleLimit;
  const isTransactionLimitReached =
    typeof transactionLimit === "number" &&
    typeof transactionUsage === "number" &&
    transactionUsage >= transactionLimit;
  const disableCreateSale =
    isPlanExpired || isSaleLimitReached || isTransactionLimitReached;

  const getSalesOrders = useCallback(
    (page: number) => {
      dispatch(
        salesActions.getSalesListRequest({
          page,
          limit,
        })
      );
    },
    [dispatch, limit]
  );

  const isRowOwner = (row: any) => {
    const createdById =
      typeof row?.createdBy === "string" ? row.createdBy : row?.createdBy?._id;
    if (!createdById || !user?._id) return false;
    return String(createdById) === String(user._id);
  };

  useEffect(() => {
    getSalesOrders(1);
  }, [getSalesOrders]);

  const handlePageChange = (page: number) => getSalesOrders(page);

  return (
    <DashboardLayout
      meta={{
        title: "Sales Orders | Invyntra Dashboard",
        description:
          "View and manage all sales orders with customer and invoice details.",
        keywords: "sales orders, invoices, customers, invyntra",
      }}
    >
      <div className="space-y-4">
        <PageHeader
          title="Sales Orders"
          breadcrumbData={[
            { label: "Home", path: "/app/dashboard" },
            { label: "Sales Orders", path: "/app/sale/list" },
          ]}
        />

        <Card>
          <CardHeader
            title="Sales Orders"
            description="Manage all orders, customers, and invoice data here."
            actions={
              canCreateSales ? (
                <Button
                  onClick={() => navigate("/app/sale/create")}
                  disabled={disableCreateSale}
                  title={
                    isPlanExpired
                      ? "Plan expired. Please upgrade."
                      : isSaleLimitReached || isTransactionLimitReached
                      ? "Order limit reached. Please upgrade."
                      : undefined
                  }
                >
                  <Plus size={16} className="mr-1" /> Create
                </Button>
              ) : null
            }
          />

          <TableV2
            columns={[
              {
                key: "orderId",
                header: "Order ID",
                accessor: ({ row }) =>
                  row.orderStatus === "draft" ? (
                    <span className="font-medium text-gray-500">
                      Order #{row.orderId} (draft)
                    </span>
                  ) : (
                    <Link
                      to={`/app/sale/${row._id}`}
                      className="font-medium text-indigo-600"
                    >
                      Order #{row.orderId}
                    </Link>
                  ),
              },
              {
                key: "customerId.name",
                header: "Customer",
                accessor: ({ row }) => row.customerId?.name || "—",
              },
              {
                key: "grandTotal",
                header: "Total Amount",
                align: "right",
                accessor: ({ row }) => (
                  <span>{currencyFormatter(row.grandTotal)}</span>
                ),
              },
              {
                key: "paymentMode",
                header: "Payment Mode",
                accessor: ({ row }) => row.paymentMode?.toUpperCase(),
              },
              {
                key: "paymentStatus",
                header: "Payment Status",
                accessor: ({ row }) => (
                  <Badge
                    text={row.paymentStatus}
                    color={
                      row.paymentStatus === "paid"
                        ? "green"
                        : row.paymentStatus === "failed"
                        ? "red"
                        : "yellow"
                    }
                  />
                ),
              },
              {
                key: "orderStatus",
                header: "Order Status",
                align: "center",
                accessor: ({ row, key }) =>
                  row[key] ? (
                    <Badge
                      color={row[key] === "completed" ? "green" : "yellow"}
                      text={row[key]}
                    />
                  ) : (
                    "-"
                  ),
              },
              {
                key: "createdBy",
                header: "Created By",
                accessor: ({ row }) => formatCreatedBy(row?.createdBy),
              },
              {
                key: "createdAt",
                header: "Date",
                align: "center",
                accessor: ({ row }) =>
                  formatDateTime(row.createdAt, "DD/MM/YYYY hh:mm:ss A"),
              },
              {
                key: "actions",
                header: "Actions",
                accessor: ({ row }) =>
                  canEditSales &&
                  row.orderStatus === "draft" &&
                  (isTenantAdmin || isRowOwner(row)) ? (
                    <div className="pr-2">
                      <button
                        className="p-2 rounded-md hover:bg-primary-100 text-primary-600"
                        onClick={() => navigate(`/app/sale/${row._id}/edit`)}
                      >
                        <Pencil size={14} />
                      </button>
                    </div>
                  ) : null,
              },
            ]}
            data={salesList?.data?.data}
            pagination={salesList?.data?.pagination}
            onPageChange={handlePageChange}
          />
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default SalesListPage;
